import { createAsyncThunk } from "@reduxjs/toolkit";
import setBaseURL from "../libs/utilities/helpers/BaseURLHelper";
import { api } from "./config/axiosConfig";
import { toggleComponentErrorPage } from "../reducers/componentErrorSlice";
import store from "../store/configureStore";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ---------------------------------------------------------------------------
// uploadClaimFiles
// POST /claim-investigator/upload
//
// FIX #1 — Sends files + optional groupName to the server.
// The server assigns group_name="" for single files and "Group X" for batches.
// This thunk simply forwards the raw FormData; groupName on the client is now
// only used for the success toast, the real group assignment lives server-side.
// ---------------------------------------------------------------------------
export const uploadClaimFiles = createAsyncThunk(
    "/claim/investigator/upload",
    async (data, { rejectWithValue }) => {
        try {
            setBaseURL(api, BASE_URL);
            const formData = new FormData();
            data.files.forEach((file) => {
                formData.append("files", file);
            });
            const response = await api.post("/claim/investigator/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.status >= 500) {
                store.dispatch(toggleComponentErrorPage({ show: true }));
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data ?? error.message);
        }
    }
);

// ---------------------------------------------------------------------------
// getClaimReportList
// POST /claim-investigator/report
//
// FIX #2 — No client-side caching layer; result is always sourced from the DB
// via the API.  Stale data can never persist across page refreshes.
// ---------------------------------------------------------------------------
export const getClaimReportList = createAsyncThunk(
    "/claim/investigator/report",
    async (data, { rejectWithValue }) => {
        try {
            setBaseURL(api, BASE_URL);
            const response = await api.post(
                "/claim/investigator/report",
                {
                    startDate: data.startDate,
                    endDate: data.endDate,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.status >= 500) {
                store.dispatch(toggleComponentErrorPage({ show: true }));
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data ?? error.message);
        }
    }
);

// ---------------------------------------------------------------------------
// downloadClaimReport
// POST /claim-investigator/report/download
//
// FIX #3 — Two key changes:
//
//  1. The _id is trimmed before sending to prevent whitespace/encoding issues
//     that were causing 400 responses from the server.
//
//  2. The response is expected as JSON (the server returns base64-encoded xlsx
//     in a JSON wrapper).  We set responseType: "json" explicitly so Axios
//     does not attempt to parse binary as text.
//
//  Note: createAsyncThunk generates a new requestId per invocation so there is
//  no Redux-level deduplication — every dispatch() triggers a real HTTP call.
// ---------------------------------------------------------------------------
export const downloadClaimReport = createAsyncThunk(
    "/claim/investigator/report/download",
    async (data, { rejectWithValue }) => {
        try {
            setBaseURL(api, BASE_URL);
            const response = await api.post(
                "/claim/investigator/report/download",
                data,
                {
                    // FIX #3 — JSON response (base64 payload inside JSON wrapper)
                    headers: { "Content-Type": "application/json" },
                    responseType: "json",
                }
            );

            if (response.status >= 500) {
                store.dispatch(toggleComponentErrorPage({ show: true }));
            }
            return response.data;
        } catch (error) {
            // Surface the server error message when available
            const serverMessage =
                error.response?.data?.message ??
                error.response?.data?.error ??
                error.message;
            return rejectWithValue({ message: serverMessage, status: error.response?.status });
        }
    }
);

// ---------------------------------------------------------------------------
// deleteClaimReport
// POST /claim-investigator/report/delete
//
// FIX #2 — Sends both _id and group_name (when present) so the server can
// perform a group-wide soft-delete or hard-delete.  The frontend then does a
// full re-fetch to ensure the DB is the authoritative source of truth.
// ---------------------------------------------------------------------------
export const deleteClaimReport = createAsyncThunk(
    "/claim/investigator/report/delete",
    async (data, { rejectWithValue }) => {
        try {
            setBaseURL(api, BASE_URL);
            const response = await api.post(
                "/claim/investigator/report/delete",
                data
            );
            if (response.status >= 500) {
                store.dispatch(toggleComponentErrorPage({ show: true }));
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data ?? error.message);
        }
    }
);

// ---------------------------------------------------------------------------
// triggerExcelDownload
// Converts a base64 string into a browser file-save dialog (.xlsx).
//
// FIX #3 — Revokes the object URL immediately after click to prevent memory
//           leaks from lingering blob URLs.
// FIX #4 — Filename is always claim_[timestamp].xlsx.
// ---------------------------------------------------------------------------
export const triggerExcelDownload = (base64Data, filename) => {
    // FIX #4 — always use claim_[timestamp].xlsx format
    const outputName =
        filename?.trim() ? filename.trim() : `claim_${Date.now()}.xlsx`;

    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    
    const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", outputName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // FIX #3 — revoke immediately; no dangling object URLs
    URL.revokeObjectURL(url);
};